// src/pages/Process.jsx
import React, { useRef } from "react";
import SEO from "../components/SEO";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import {
  Clock,
  Check,
  Flame,
  Droplets,
  Thermometer,
  Timer,
  Scale,
  ArrowDown,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

/* --- THEME CONFIGURATION --- */
const THEME = {
  blue: "#0087E6",
  navy: "#0F172A",
  sky: "#F0F9FF",
  white: "#FFFFFF",
  gold: "#FFD700",
};

/* --- DATA: THE SPECS --- */
const SPECS = [
  {
    label: "Hydration",
    value: "70%",
    desc: "Light feel, soft bite.",
    icon: Droplets,
  },
  {
    label: "Fermentation",
    value: "72h",
    desc: "Slow time, better flavour.",
    icon: Clock,
  },
  {
    label: "Oven Temp",
    value: "485°C",
    desc: "High heat, fast finish.",
    icon: Thermometer,
  },
  {
    label: "Cook Time",
    value: "90s",
    desc: "Quick bake, great texture.",
    icon: Timer,
  },
];

/* -----------------------------
   HERO
------------------------------ */
const ProcessHero = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.5], [0.55, 0.9]);

  return (
    <header
      ref={containerRef}
      className="relative w-full h-[85vh] min-h-[580px] overflow-hidden bg-[#0F172A] flex items-center justify-center"
    >
      {/* Background */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1576458088443-04a19bb13da6?q=80&w=2200&auto=format&fit=crop"
          alt="Flour dust"
          className="w-full h-full object-cover opacity-80"
          loading="eager"
        />
      </motion.div>

      {/* Overlay */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-[1] bg-gradient-to-b from-[#0F172A]/85 via-[#0F172A]/45 to-[#0F172A]"
      />

      {/* Content */}
      <div className="relative z-10 text-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 rounded-full text-white/65 text-[10px] font-bold uppercase tracking-widest mb-6 backdrop-blur-md">
            <Scale size={12} />
            The Ritual
          </div>

          <h1 className="font-anton text-6xl sm:text-8xl md:text-9xl text-white uppercase leading-[0.8] mb-6">
            The <br /> Process.
          </h1>

          <p className="font-serif italic text-white/80 text-xl sm:text-2xl max-w-2xl mx-auto">
            “Not complicated. Just done properly, every single day.”
          </p>

          <div className="mt-10 flex justify-center">
            <a
              href="#specs"
              className="group inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/10 border border-white/15 text-white/80 hover:text-white hover:bg-white/15 transition-colors"
            >
              <span className="text-xs font-bold uppercase tracking-widest">
                Scroll
              </span>
              <ArrowDown
                size={16}
                className="group-hover:translate-y-0.5 transition-transform"
              />
            </a>
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-[#0F172A] to-transparent z-20" />
    </header>
  );
};

/* -----------------------------
   TECH SPECS GRID
------------------------------ */
const TechSpecs = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <section
      id="specs"
      className="bg-[#0F172A] pb-20 sm:pb-28 relative z-30"
      ref={ref}
    >
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-10 border-t border-white/10 pt-12 sm:pt-16">
          {SPECS.map((spec, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 18 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              className="group"
            >
              <div className="mb-4 text-[#0087E6] opacity-50 group-hover:opacity-100 transition-opacity">
                <spec.icon size={24} />
              </div>

              <div className="font-anton text-4xl sm:text-6xl text-white mb-2">
                {spec.value}
              </div>

              <div className="text-sm font-bold uppercase tracking-widest text-[#0087E6] mb-1">
                {spec.label}
              </div>

              <div className="text-xs text-white/45 font-manrope">{spec.desc}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* -----------------------------
   STEP (Reusable)
------------------------------ */
const Step = ({
  id,
  phase,
  kicker,
  title,
  highlight,
  body,
  bullets,
  img,
  bg = "white",
  dark = false,
  badgeLabel,
  badgeValue,
  badgeIcon: BadgeIcon,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-120px" });

  const bgClass =
    bg === "white" ? "bg-white" : bg === "sky" ? "bg-[#F0F9FF]" : "bg-[#0F172A]";

  const textMain = dark ? "text-white" : "text-[#0F172A]";
  const textSub = dark ? "text-white/80" : "text-gray-600";
  const kickerColor = dark ? "text-[#0087E6]" : "text-[#0F172A]";
  const phaseNumColor = dark ? "text-[#0087E6]/40" : "text-[#0087E6]/20";

  return (
    <section
      id={id}
      className={`py-20 sm:py-32 overflow-hidden ${bgClass} ${
        dark ? "relative" : ""
      }`}
      ref={ref}
    >
      {dark && <div className="absolute inset-0 bg-[#0087E6]/5" />}

      <div className="container mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          {/* Text */}
          <motion.div
            initial={{ opacity: 0, x: -26 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className={`font-anton text-6xl ${phaseNumColor}`}>
                {phase}
              </span>
              <span
                className={`font-manrope font-bold text-xs uppercase tracking-[0.2em] ${kickerColor}`}
              >
                {kicker}
              </span>
            </div>

            <h2
              className={`font-anton text-4xl sm:text-5xl md:text-6xl uppercase mb-6 leading-none ${textMain}`}
            >
              {title} <br />
              <span className={dark ? "text-[#FFD700]" : "text-[#0087E6]"}>
                {highlight}
              </span>
            </h2>

            <div
              className={`space-y-6 font-manrope text-lg leading-relaxed ${textSub}`}
            >
              {body.map((p, idx) => (
                <p key={idx}>{p}</p>
              ))}
            </div>

            {bullets?.length ? (
              <div
                className={`mt-8 flex flex-wrap gap-4 text-xs font-bold uppercase tracking-widest ${
                  dark ? "text-[#60b8ff]" : "text-[#0087E6]"
                }`}
              >
                {bullets.map((b, idx) => (
                  <span key={idx} className="flex items-center gap-2">
                    <Check size={14} />
                    {b}
                  </span>
                ))}
              </div>
            ) : null}
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="relative"
          >
            <div
              className={`relative aspect-square sm:aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl ${
                dark ? "border border-white/10" : ""
              }`}
            >
              <img
                src={img}
                alt={`${title} ${highlight}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div
                className={`absolute inset-0 ${
                  dark
                    ? "bg-gradient-to-t from-[#0F172A] to-transparent opacity-65"
                    : "bg-[#0F172A]/10 mix-blend-multiply"
                }`}
              />
            </div>

            {badgeLabel && badgeValue ? (
              <div className="absolute top-6 right-6 bg-white/92 backdrop-blur p-4 rounded-xl shadow-lg border border-blue-50">
                <div className="flex items-center gap-2">
                  {BadgeIcon ? (
                    <BadgeIcon size={14} className="text-[#0087E6]" />
                  ) : null}
                  <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                    {badgeLabel}
                  </div>
                </div>
                <div className="font-anton text-3xl text-[#0087E6]">
                  {badgeValue}
                </div>
              </div>
            ) : null}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* -----------------------------
   PAGE
------------------------------ */
export default function Process() {
  return (
    <div className="bg-white min-h-screen w-full overflow-x-hidden selection:bg-[#0087E6] selection:text-white">
      <SEO
        title="The Process — How D10’s Makes Pizza"
        description="How D10’s makes pizza: slow fermentation, hand shaping, and a fast high-heat finish. Simple ingredients, clean execution, good nights."
        image="/og-preview.png"
        canonical="https://www.d10s.pizza/process"
        type="website"
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Anton&family=Manrope:wght@400;500;700;800&family=Playfair+Display:ital,wght@0,400;1,400;1,700&display=swap');
        .font-anton { font-family: 'Anton', sans-serif; }
        .font-manrope { font-family: 'Manrope', sans-serif; }
        .font-serif { font-family: 'Playfair Display', serif; }

        html { scroll-behavior: smooth; -webkit-tap-highlight-color: transparent; }
        body { overflow-x: hidden; }
        button, a { min-height: 44px; }
        :focus-visible { outline: 2px solid ${THEME.blue}; outline-offset: 2px; }

        /* Optional: keep custom scrollbar, but make it subtle */
        ::-webkit-scrollbar { width: 10px; }
        ::-webkit-scrollbar-track { background: ${THEME.sky}; }
        ::-webkit-scrollbar-thumb { background: ${THEME.blue}; border-radius: 8px; }
        ::-webkit-scrollbar-thumb:hover { background: ${THEME.navy}; }
      `}</style>

      <Navbar />

      <main>
        <ProcessHero />
        <TechSpecs />

        <Step
          id="time"
          phase="01"
          kicker="Phase One"
          title="Give it"
          highlight="Time."
          body={[
            "The biggest “secret” is boring. We wait. A long, controlled fermentation builds flavour and makes the dough feel lighter to eat.",
            "That 72-hour rhythm keeps everything calm and consistent. Better structure. Cleaner bite. Less heaviness after.",
          ]}
          bullets={["Slow fermentation", "Light feel"]}
          img="https://images.unsplash.com/photo-1585238342024-78d387f4a707?q=80&w=1200&auto=format&fit=crop"
          bg="white"
          badgeLabel="Fermentation"
          badgeValue="72h"
          badgeIcon={Clock}
        />

        <Step
          id="hands"
          phase="02"
          kicker="Phase Two"
          title="Hands"
          highlight="Only."
          body={[
            "No rolling pins. We shape by hand so the dough keeps the air it earned during fermentation.",
            "The center stays thin, the rim gets its lift, and the whole pizza lands balanced: soft, structured, and easy to finish.",
          ]}
          bullets={["Hand-shaped", "Balanced rim"]}
          img="https://images.unsplash.com/photo-1513104890138-7c749659a591?q=80&w=1200&auto=format&fit=crop"
          bg="sky"
        />

        <Step
          id="heat"
          phase="03"
          kicker="Phase Three"
          title="High heat"
          highlight="Finish."
          body={[
            "This is the moment. The oven is hot, the bake is fast, and the margin for error is tiny (which is why we like it).",
            "In about 90 seconds you get the texture we’re chasing: airy edge, soft middle, and that little char that makes it taste like a proper night out on Prosika.",
          ]}
          bullets={["485°C oven", "90-second bake"]}
          img="https://images.unsplash.com/photo-1574355621621-027e6eb2e914?q=80&w=1200&auto=format&fit=crop"
          bg="navy"
          dark
          badgeLabel="Bake time"
          badgeValue="90s"
          badgeIcon={Timer}
        />
      </main>

      <Footer />
    </div>
  );
}
